import { QueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { getErrorMessage } from '@billda/shared-ui';
import { useSnackbarStore } from '../stores/snackbarStore';

/**
 * Read the HTTP status off either an AxiosError (`response.status`) or an
 * ApiError from the generated @billda/api client (`status`, duck-typed via
 * `name === 'ApiError'` like getErrorMessage does), so migrated call sites
 * behave identically whether they threw an axios error or an ApiError.
 */
export function getHttpStatus(error: unknown): number | undefined {
  const axiosError = error as AxiosError;
  if (axiosError?.response?.status) {
    return axiosError.response.status;
  }
  const apiError = error as { name?: string; status?: number };
  if (apiError?.name === 'ApiError' && typeof apiError.status === 'number') {
    return apiError.status;
  }
  return undefined;
}

/**
 * Detect a transport-level failure that has no HTTP status: an axios network
 * error (ECONNREFUSED/ETIMEDOUT/ECONNABORTED) or the generated client's raw
 * fetch/timeout rejection (TypeError / AbortError / TimeoutError).
 */
export function isNetworkError(error: unknown): boolean {
  const axiosError = error as AxiosError;
  if (
    axiosError?.code === 'ECONNREFUSED' ||
    axiosError?.code === 'ETIMEDOUT' ||
    axiosError?.code === 'ECONNABORTED'
  ) {
    return true;
  }
  const named = error as { name?: string };
  if (
    named?.name === 'AbortError' ||
    named?.name === 'TimeoutError' ||
    named?.name === 'TypeError'
  ) {
    return true;
  }
  return false;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry if we've already tried too many times
        if (failureCount >= 2) {
          return false;
        }

        const status = getHttpStatus(error);

        // The client (axios interceptor / generated middleware) handles these
        // infrastructure/auth errors — don't double-retry.
        const clientHandledStatuses = [401, 403, 503, 504];
        if (status && clientHandledStatuses.includes(status)) {
          return false;
        }

        // Network errors (no HTTP status) are handled by the client, not retried here.
        if (isNetworkError(error)) {
          return false;
        }

        // Retry server errors (5xx) that the client doesn't handle
        if (status && status >= 500) {
          return true;
        }

        // Don't retry client errors (4xx) or other errors
        return false;
      },
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes default
    },
    mutations: {
      retry: false, // Never retry mutations automatically
      onError: (error: Error) => {
        // Auth errors are handled by the client interceptor — don't toast
        const status = getHttpStatus(error);
        if (status === 401 || status === 403) {
          return;
        }
        useSnackbarStore
          .getState()
          .showSnackbar(getErrorMessage(error), 'error');
      },
    },
  },
});

/**
 * Clears all React Query caches.
 * Use this when switching accounts or logging out to ensure stale data is removed.
 */
export function clearAllCaches(): void {
  queryClient.clear();
}

/**
 * Invalidates all React Query caches to force refetch.
 * Use this when token changes to ensure all queries refetch with the new token.
 */
export function invalidateAllQueries(): void {
  queryClient.invalidateQueries();
}
